import { config } from '../config';
import { headers, APIResponse, stleaf, fetchAPI, SpanTag } from '../helpers';

interface CreateAssessmentParams {
  name: string;
  missionId: string;
  mediaIds: string[];
  assessmentTypeId: string;
}
export const createAssessment = (
  st: SpanTag,
  params: CreateAssessmentParams,
): Promise<APIResponse> => {
  return fetchAPI(stleaf(st, 'uc3zoh'), `${config.API_URL}/v1/assessments`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

export const getAssessment = (
  st: SpanTag,
  id: string,
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'lee4ub'),
    `${config.API_URL}/v1/assessments/${id}`,
    {
      method: 'GET',
      credentials: 'include',
      headers,
    },
  );
};

export const deleteAssessment = (
  st: SpanTag,
  id: string,
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'uy9eso'),
    `${config.API_URL}/v1/assessments/${id}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers,
    },
  );
};

interface CreateAOIParams {
  mediaId: string;
  severity: string;
  categoryId: string;
  comments?: string;
  recommendedAction?: string;
  coordinates: number[][];
}
export const createAOI = (
  st: SpanTag,
  assessmentId: string,
  params: CreateAOIParams,
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'aesh1e'),
    `${config.API_URL}/v1/assessments/${assessmentId}/aois`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(params),
    },
  );
};

interface UpdateAOIParams {
  mediaId: string;
  severity?: string;
  categoryId?: string;
  comments?: string;
  recommendedAction?: string;
  coordinates?: number[][];
}
export const updateAOI = (
  st: SpanTag,
  assessmentId: string,
  aoiId: string,
  params: UpdateAOIParams,
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'ti9aen'),
    `${config.API_URL}/v1/assessments/${assessmentId}/aois/${aoiId}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify(params),
    },
  );
};

export const deleteAOI = (
  st: SpanTag,
  assessmentId: string,
  aoiId: string,
  params: { mediaId: string },
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'aik2su'),
    `${config.API_URL}/v1/assessments/${assessmentId}/aois/${aoiId}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers,
      body: JSON.stringify(params),
    },
  );
};

interface SetNoAOIParams {
  mediaId: string;
  status: string;
}
export const setNoAOI = (
  st: SpanTag,
  assessmentId: string,
  params: SetNoAOIParams,
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'sei8wo'),
    `${config.API_URL}/v1/assessments/${assessmentId}/aois/`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(params),
    },
  );
};

interface LinkAOIParams {
  mediaId: string;
  coordinates: number[][];
  comments?: string;
}
export const linkAOI = (
  st: SpanTag,
  assessmentId: string,
  aoiId: string,
  params: LinkAOIParams,
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'sha6ye'),
    `${config.API_URL}/v1/assessments/${assessmentId}/aois/${aoiId}/link`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(params),
    },
  );
};

export const getAssessmentAOI = (
  st: SpanTag,
  assessmentId: string,
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'oht4oh'),
    `${config.API_URL}/v1/assessments/${assessmentId}/aois`,
    {
      method: 'GET',
      credentials: 'include',
      headers,
    },
  );
};

export const updateMediaAction = (
  st: SpanTag,
  assessmentId: string,
  mediaId: string,
  assessmentAction: 'none-detected' | 'skipped',
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'ab9aen'),
    `${config.API_URL}/v1/assessments/${assessmentId}/media/${mediaId}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify({ assessmentAction }),
    },
  );
};

export interface GenerateReportParams {
  name: string;
}
export const generateAssessmentReport = (
  st: SpanTag,
  assessmentId: string,
  params: GenerateReportParams,
): Promise<APIResponse<{ id: string }>> => {
  return fetchAPI(
    stleaf(st, 'mee6yu'),
    `${config.API_URL}/v1/assessments/${assessmentId}/report-pdf`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(params),
    },
  );
};

export const getAssessmentReport = <T>(
  st: SpanTag,
  assessmentId: string,
  reportId: string,
): Promise<APIResponse<T>> => {
  return fetchAPI(
    stleaf(st, 'oop5ag'),
    `${config.API_URL}/v1/assessments/${assessmentId}/report/${reportId}`,
    {
      method: 'GET',
      credentials: 'include',
      headers,
    },
  );
};

export const getReportURL = async (
  st: SpanTag,
  assessmentId: string,
  reportId: string,
): Promise<string> => {
  const r = await fetchAPI(
    stleaf(st, 'loh8ko'),
    `${config.API_URL}/v1/assessments/${assessmentId}/report/${reportId}/download`,
    {
      method: 'GET',
      credentials: 'include',
      headers,
    },
  );
  return r.body;
};

export const deleteReport = (
  st: SpanTag,
  assessmentId: string,
  reportId: string,
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'ties3p'),
    `${config.API_URL}/v1/assessments/${assessmentId}/report/${reportId}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers,
    },
  );
};
